import { DataImport } from "@/hooks/useDataImports";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, AlertCircle, CheckCircle2, Clock, Loader2, AlertTriangle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format } from "date-fns";

interface ImportHistoryTableProps {
  imports: DataImport[];
  onResolveConflicts?: (importId: string) => void;
}

export function ImportHistoryTable({ imports, onResolveConflicts }: ImportHistoryTableProps) {
  const getStatusBadge = (status: DataImport['status']) => {
    const statusConfig = {
      pending: { label: 'Aguardando', icon: Clock, variant: 'secondary' as const, color: 'text-yellow-600' },
      processing: { label: 'Processando', icon: Loader2, variant: 'default' as const, color: 'text-blue-600' },
      conflicts: { label: 'Conflitos', icon: AlertTriangle, variant: 'destructive' as const, color: 'text-orange-600' },
      success: { label: 'Concluído', icon: CheckCircle2, variant: 'default' as const, color: 'text-success' },
      error: { label: 'Erro', icon: AlertCircle, variant: 'destructive' as const, color: 'text-destructive' },
    };

    const config = statusConfig[status];
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className={`h-3 w-3 ${config.color} ${status === 'processing' ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data/Hora</TableHead>
            <TableHead>Arquivo</TableHead>
            <TableHead>Usuário</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Linhas</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {imports.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground">
                Nenhuma importação realizada ainda
              </TableCell>
            </TableRow>
          ) : (
            imports.map((imp) => (
              <TableRow key={imp.id}>
                <TableCell className="font-medium">
                  {format(new Date(imp.created_at), 'dd/MM/yyyy HH:mm')}
                </TableCell>
                <TableCell>{imp.file_name}</TableCell>
                <TableCell>{imp.profiles?.full_name || 'Desconhecido'}</TableCell>
                <TableCell>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="inline-block">
                          {getStatusBadge(imp.status)}
                        </div>
                      </TooltipTrigger>
                      {imp.error_message && (
                        <TooltipContent className="max-w-md">
                          <p className="whitespace-pre-wrap text-sm">{imp.error_message}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell className="text-right">
                  {imp.rows_processed !== null && imp.rows_total !== null
                    ? `${imp.rows_processed}/${imp.rows_total}`
                    : '-'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {imp.status === 'conflicts' && onResolveConflicts && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onResolveConflicts(imp.id)}
                      >
                        <AlertTriangle className="h-4 w-4 mr-1" />
                        Resolver
                      </Button>
                    )}
                    {imp.file_url && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(imp.file_url!, '_blank')}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
